// src/pages/PartnerBusinessInfo.tsx
'use client';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export default function PartnerBusinessInfo() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    brand_name: "",
    logo_url: "",
    business_type: "gym",
    country: "GB",
  });

  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("partner_accounts")
        .select("first_name, last_name, brand_name, logo_url, business_type, country")
        .eq("user_id", user.id)
        .single();

      if (data) {
        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          brand_name: data.brand_name || "",
          logo_url: data.logo_url || "",
          business_type: data.business_type || "gym",
          country: data.country || "GB",
        });
      }
    })();
  }, []);

  const save = async () => {
    if (!form.first_name || !form.last_name || !form.brand_name) {
      alert("Please fill in your name and brand name");
      return;
    }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("partner_accounts")
      .upsert({
        user_id: user?.id,
        ...form,
      });

    setSaving(false);
    if (error) {
      alert("Save failed: " + error.message);
    } else {
      navigate("/partner/choose-tier");
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a] overflow-y-auto">
      <main className="flex-1 overflow-y-auto p-6 md:p-8">
        <div className="max-w-4xl mx-auto">

          {/* Progress bar */}
          <div className="max-w-md mx-auto mb-10">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>Step 1 of 3</span>
              <span>Business details</span>
            </div>
            <div className="h-1.5 bg-[#f1f5f9] rounded-full overflow-hidden">
              <div className="h-full bg-[#a78bfa] rounded-full transition-all duration-500" style={{ width: '33%' }} />
            </div>
          </div>

          {/* Header */}
          <header className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold">Business Details</h1>
            <p className="text-lg text-gray-500 mt-3">Tell us about you and your brand</p>
          </header>

          {/* Form Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 space-y-8">

            {/* First & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">First Name</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="Alex"
                  className="w-full px-4 py-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Last Name</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Smith"
                  className="w-full px-4 py-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition"
                />
              </div>
            </div>

            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Brand / Gym Name</label>
              <input
                type="text"
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                placeholder="Alpha Performance"
                className="w-full px-4 py-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition"
              />
            </div>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Logo URL (optional)</label>
              <input
                type="text"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://yourlogo.com/logo.png"
                className="w-full px-4 py-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition"
              />
            </div>

            {/* Business Type */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Business Type</label>
              <select
                value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] focus:border-[#a78bfa] focus:outline-none transition"
              >
                <option value="gym">Gym / Studio</option>
                <option value="coach">Personal Trainer / Coach</option>
                <option value="team">Sports Team</option>
                <option value="online">Online Coach</option>
              </select>
            </div>

            {/* Smaller, elegant Continue Button */}
<div className="pt-6">
  <button
    onClick={save}
    disabled={saving || !form.first_name || !form.last_name || !form.brand_name}
    className="w-full py-4 bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-bold text-lg rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
  >
    {saving ? "Saving..." : "Continue"}
  </button>
</div>

          </div>

          {/* Footer */}
          <footer className="mt-12 text-center text-gray-500 text-xs">
            © {new Date().getFullYear()} Input. All rights reserved.
          </footer>

        </div>
      </main>
    </div>
  );
}
